import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Play, Plus, Trash2, Loader2, ClipboardList, ShieldAlert, Megaphone, Timer, X } from "lucide-react";

const KINDS = [
  { key: "submit_result",  label: "Simulate transmission", icon: ClipboardList, color: "#5AC8FA" },
  { key: "create_incident", label: "Log incident",         icon: ShieldAlert,   color: "#FF9F0A" },
  { key: "broadcast_sms",  label: "Fire SMS broadcast",    icon: Megaphone,     color: "#AF52DE" },
  { key: "wait",           label: "Wait",                  icon: Timer,         color: "#71717A" },
];

const CATS = ["violence","ballot_snatching","over_voting","obstruction","equipment_malfunction","bribery","intimidation","misconduct","media_denied","underage_voting","impersonation","other"];
const SEVS = ["low","medium","high","critical"];

export default function Playbooks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {name, description, steps:[]}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/playbooks"); setItems(data); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", description: "", steps: [
    { kind: "submit_result", delay_seconds: 0 },
    { kind: "wait", delay_seconds: 5 },
    { kind: "create_incident", delay_seconds: 0, category: "over_voting", severity: "high", title: "Drill incident" },
  ]});

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Give the playbook a name");
    if (!editing.steps.length) return toast.error("Add at least one step");
    try {
      await api.post("/playbooks", editing);
      toast.success("Playbook saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this playbook?")) return;
    try { await api.delete(`/playbooks/${pid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const run = async (pb) => {
    try {
      const { data } = await api.post(`/playbooks/${pb.id}/run`);
      toast.success(`Running "${pb.name}" — ${data.steps} step${data.steps===1?"":"s"} scheduled`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Run failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Rehearsal</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="playbooks-title">Playbooks</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Scripted sequences of transmissions, incidents, and broadcasts. Fire the same drill every rehearsal, exactly the same way.</p>
        </div>
        <button onClick={startNew} data-testid="new-playbook-btn"
          className="px-4 py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200 flex items-center gap-2">
          <Plus size={14}/> New playbook
        </button>
      </div>

      <div className="mt-8 border border-white/10 data-table" data-testid="playbooks-list">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-1 text-center">Steps</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        <div className="divide-y divide-white/5">
          {loading && <div className="p-4 text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>}
          {!loading && !items.length && <div className="p-6 text-zinc-500 text-sm">No playbooks yet. Create your first drill script above.</div>}
          {items.map(pb => (
            <div key={pb.id} className="grid grid-cols-12 px-4 py-3 items-center hover:bg-white/[0.02]" data-testid={`playbook-${pb.id}`}>
              <div className="col-span-4 truncate">{pb.name}</div>
              <div className="col-span-4 text-xs text-zinc-400 truncate">{pb.description || "—"}</div>
              <div className="col-span-1 text-center tabular text-sm">{(pb.steps || []).length}</div>
              <div className="col-span-2 text-xs text-zinc-500">{(pb.created_at||"").replace("T"," ").slice(0,16)}</div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={()=>run(pb)} data-testid={`run-${pb.id}`} className="p-1.5 text-emerald-400 hover:bg-emerald-500/10" title="Run"><Play size={14}/></button>
                <button onClick={()=>remove(pb.id)} data-testid={`delete-${pb.id}`} className="p-1.5 text-zinc-500 hover:text-red-400" title="Delete"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setEditing(null)}>
          <div onClick={e=>e.stopPropagation()} className="relative bg-[#0F0F12] border border-white/15 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" data-testid="playbook-editor">
            <button onClick={()=>setEditing(null)} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white"><X size={16}/></button>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">New playbook</div>
            <h2 className="font-display text-3xl mt-2">Script your drill</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
              <input placeholder="Name" value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})}
                data-testid="playbook-name" className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
              <input placeholder="Short description (optional)" value={editing.description} onChange={e=>setEditing({...editing, description:e.target.value})}
                className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
            </div>

            <div className="mt-6 space-y-2">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Steps ({editing.steps.length})</div>
              {editing.steps.map((s, i) => {
                const kind = KINDS.find(k => k.key === s.kind) || KINDS[0];
                const Icon = kind.icon;
                return (
                  <div key={i} className="border border-white/10 p-3 grid grid-cols-12 gap-2 items-center" data-testid={`step-${i}`}>
                    <div className="col-span-1 text-center"><Icon size={16} style={{ color: kind.color }}/></div>
                    <select value={s.kind} onChange={e=>{ const st=[...editing.steps]; st[i]={...st[i], kind:e.target.value}; setEditing({...editing, steps: st}); }}
                      className="col-span-3 bg-transparent border border-white/20 px-2 py-1.5 text-xs outline-none">
                      {KINDS.map(k => <option key={k.key} value={k.key} className="bg-black">{k.label}</option>)}
                    </select>
                    <label className="col-span-2 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      Delay
                      <input type="number" min={0} max={300} value={s.delay_seconds} onChange={e=>{ const st=[...editing.steps]; st[i]={...st[i], delay_seconds: Math.max(0, +e.target.value || 0)}; setEditing({...editing, steps: st}); }}
                        className="w-14 bg-transparent border border-white/20 px-1.5 py-0.5 text-xs outline-none tabular"/>s
                    </label>
                    <div className="col-span-5 flex gap-2">
                      {s.kind === "create_incident" && (
                        <>
                          <select value={s.category || "other"} onChange={e=>{ const st=[...editing.steps]; st[i]={...st[i], category:e.target.value}; setEditing({...editing, steps: st}); }}
                            className="bg-transparent border border-white/20 px-2 py-1 text-xs outline-none flex-1">
                            {CATS.map(c => <option key={c} value={c} className="bg-black">{c}</option>)}
                          </select>
                          <select value={s.severity || "medium"} onChange={e=>{ const st=[...editing.steps]; st[i]={...st[i], severity:e.target.value}; setEditing({...editing, steps: st}); }}
                            className="bg-transparent border border-white/20 px-2 py-1 text-xs outline-none">
                            {SEVS.map(v => <option key={v} value={v} className="bg-black">{v}</option>)}
                          </select>
                        </>
                      )}
                      {s.kind === "broadcast_sms" && (
                        <input placeholder="Message" value={s.message || ""} onChange={e=>{ const st=[...editing.steps]; st[i]={...st[i], message:e.target.value}; setEditing({...editing, steps: st}); }}
                          className="bg-transparent border border-white/20 px-2 py-1 text-xs outline-none flex-1"/>
                      )}
                    </div>
                    <button onClick={()=>{ const st=editing.steps.filter((_,idx)=>idx!==i); setEditing({...editing, steps: st}); }}
                      data-testid={`remove-step-${i}`} className="col-span-1 text-zinc-500 hover:text-red-400"><Trash2 size={14}/></button>
                  </div>
                );
              })}
              <button onClick={()=>setEditing({...editing, steps:[...editing.steps, { kind:"wait", delay_seconds: 5 }]})} data-testid="add-step-btn"
                className="w-full py-2 border border-dashed border-white/20 text-xs uppercase tracking-wider text-zinc-400 hover:bg-white/[0.03] flex items-center justify-center gap-2">
                <Plus size={13}/> Add step
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={()=>setEditing(null)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button onClick={save} data-testid="save-playbook-btn" className="px-4 py-2 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200">Save playbook</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
